import { components } from '@doltech/pageManagement/dto';
import { Modal } from '@doltech/ui/lib/figma/DesignSystemV2/Modal/Modal';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { fetchLatestUserTests } from '@doltech/ui/lib/figma/Layout/Landing/api/user-test-landing';
import { TestStatusEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { secondsToMinuteFormat } from '@doltech/utils/lib/date';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { useMutation, useQuery } from 'react-query';

import { startOrResumeFullTest, startOrResumePracticeTest } from '../api/do-test';
import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { ModalType } from '../model/test-submissions.store';

interface UserTestOverview {
  inCompletedQuestions: number;
  remainingTimeInMinute: number;
}

const useStartNewTestSession = ({ onStartTestSuccess, trialUserId }) => {
  const { testID, testType, hideModal } = useTestSubmissionStore();
  const { mutate: startOrResumePracticeTestFn, isLoading: isLoadingPraticeTest } = useMutation(startOrResumePracticeTest, {
    onSuccess: onStartTestSuccess,
  });

  const { mutate: startOrResumeFullTestFn, isLoading: isLoadingFullTest } = useMutation(startOrResumeFullTest, {
    onSuccess: onStartTestSuccess,
  });

  const startOrResumeTest = React.useCallback(
    async (forceRestart = false) => {
      if (testType === TestTypeEnum.FULL_TEST) {
        startOrResumeFullTestFn({ testId: testID, forceRestart, trialUserId });
      } else {
        startOrResumePracticeTestFn({
          testSectionId: testID,
          forceRestart,
          trialUserId,
        });
      }
    },
    [startOrResumeFullTestFn, startOrResumePracticeTestFn, testID, testType, trialUserId]
  );

  const startNewTestSession = React.useCallback(() => {
    hideModal();
    startOrResumeTest(true);
  }, [hideModal, startOrResumeTest]);

  const continueCurrentSection = React.useCallback(async () => {
    hideModal();
    await startOrResumeTest(false);
  }, [hideModal, startOrResumeTest]);

  return { startNewTestSession, continueCurrentSection, isLoading: isLoadingPraticeTest || isLoadingFullTest };
};

const isUserHasInProgressTest = (userTest: components['schemas']['UserTestOverviewDTO']) => {
  return userTest && userTest?.status === TestStatusEnum.IN_PROGRESS;
};

const useUserTestOverview = (
  enabled: boolean,
  startNewTestSession: () => void,
  isListening: boolean,
  trialUserId?: string
) => {
  const idRef = React.useRef(nanoid());
  const { openModal, testType, testID: testOrTestSectionId } = useTestSubmissionStore();
  const [overview, setOverview] = React.useState<UserTestOverview>();
  let userTest: components['schemas']['UserPracticeTestOverviewDTO'] &
    components['schemas']['UserFullTestOverviewDTO'] = null;
  const { isFetched, data } = useQuery(
    ['getUserTestsQuery', testOrTestSectionId, idRef.current],
    () =>
      fetchLatestUserTests(
        testType,
        testType === TestTypeEnum.FULL_TEST ? [testOrTestSectionId] : undefined,
        testType === TestTypeEnum.PRACTICE_TEST ? [testOrTestSectionId] : undefined,
        trialUserId
      ),
    {
      enabled,
      keepPreviousData: false,
    }
  );
  userTest = data?.content?.[0];

  // Populate overview data to show in modal
  React.useEffect(() => {
    if (isFetched && enabled) {
      if (isUserHasInProgressTest(userTest)) {
        setOverview({
          inCompletedQuestions: userTest.totalQuestions - userTest.totalAnsweredQuestions,
          remainingTimeInMinute:
            testType === TestTypeEnum.FULL_TEST
              ? secondsToMinuteFormat(
                  userTest.test?.totalDuration - userTest.testTakenTimeInSeconds
                )
              : Math.floor(
                  userTest.testSection?.totalDuration - userTest.testTakenTimeInSeconds / 60
                ),
        });
        openModal(ModalType.CONTINUE_OR_START_OVER);
      } else {
        if (isListening) {
          setOverview(null);
          openModal(ModalType.CONTINUE_OR_START_OVER);
        } else {
          startNewTestSession();
        }
      }
    }
  }, [enabled, startNewTestSession, userTest, isFetched, openModal, testType, isListening]);

  return { overview, userTest };
};

interface ContinueOrStarOverModalProps {
  onStartTestSuccess: (data?: any) => void;
  enabled: boolean;
  trialUserId?: string;
}

export const ContinueOrStarOverModal = React.memo(
  ({ onStartTestSuccess, enabled, trialUserId }: ContinueOrStarOverModalProps) => {
    const testSubmissionStore = useTestSubmissionStore();
    const { startNewTestSession, continueCurrentSection, isLoading } = useStartNewTestSession({
      onStartTestSuccess,
      trialUserId,
    });

    const { modal, skill } = testSubmissionStore;
    const isListening = skill === 'LISTENING';

    const { overview } = useUserTestOverview(
      enabled,
      startNewTestSession,
      isListening,
      trialUserId
    );
    const isVisible = ModalType.CONTINUE_OR_START_OVER === modal;

    if (!overview?.remainingTimeInMinute) {
      return (
        <Modal.Basic
          title={'Bắt đầu'}
          onOk={continueCurrentSection}
          okButtonDisabled={isLoading}
          onCancel={null}
          visible={isVisible}
          okText="Start"
          cancelText={null}
          closable={false}
          className="restart-test-confirm-modal"
        >
          <Typography.Text variant="regular/16">Bấm vào start khi bạn đã sẵn sàng</Typography.Text>
        </Modal.Basic>
      );
    }

    return (
      <Modal.Basic
        title="Tiếp tục làm bài"
        onOk={continueCurrentSection}
        onCancel={null}
        visible={isVisible}
        okButtonDisabled={isLoading}
        okText="Làm tiếp"
        cancelText={null}
        closable={false}
        className="restart-test-confirm-modal"
      >
        {overview?.remainingTimeInMinute > 0 ? (
          <Typography.Text variant="regular/16">
            Bạn còn <b>{overview?.inCompletedQuestions} câu</b> trong vòng{' '}
            <b>{overview?.remainingTimeInMinute} phút</b>. Bạn có thể chọn làm tiếp.
          </Typography.Text>
        ) : (
          <Typography.Text variant="regular/16">
            Bạn còn <b>{overview?.inCompletedQuestions} câu</b>. Bạn có thể chọn làm tiếp.
          </Typography.Text>
        )}
      </Modal.Basic>
    );
  }
);
